import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
} from '@shared/schemas';

import type { TypeCategories } from '../domain/profile-detail.rules';
import {
  emptyAddToList,
  emptyContactStatus,
  emptyDeactivate,
  emptyEnrich,
  emptyLicenceTypesEdit,
  emptyNotes,
  emptyReactivate,
  emptyTypesEdit,
  initialState,
} from './profile-detail.state';

/** Payload of the notes transitions (#676): which profile the notes belong to. */
export interface NotesLoaded {
  profileId: string;
  notes: ProfileNotes;
}

export interface NotesFailed {
  profileId: string;
  message: string;
}

/**
 * Profile Detail slice. Reducers are pure state transitions only; the initial
 * load, the silent refresh and the enrich POST all live in the saga, which
 * dispatches these `load*` / `enrich*` transitions as it progresses.
 * `loadStarted` keeps the current `profile` so a re-load (refresh after a tag
 * edit or a completed enrichment) never blanks the page. `favoriteChanged`
 * patches the in-place favourite flag from the shared `FavoriteButton`. The
 * Network tab has its own load pair, driven on each entry into that tab.
 */
const slice = createSlice({
  name: 'profileDetail',
  initialState,
  reducers: {
    // ── Profile load lifecycle ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<ProfileResponse>) => {
      state.profile = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Enrichment lifecycle ──
    /** POST is in flight. */
    enrichStarted: (state) => {
      state.enrich = { status: 'enriching', error: null };
    },
    /** POST accepted: the live `EnrichmentPanel` drawer opens and takes over. */
    enrichAccepted: (state) => {
      state.enrich = { status: 'accepted', error: null };
    },
    enrichFailed: (state, action: PayloadAction<string>) => {
      state.enrich = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled enrichment (page calls after the panel closes). */
    enrichCleared: (state) => {
      state.enrich = emptyEnrich;
    },

    // ── Deactivation lifecycle ──
    /** POST is in flight. */
    deactivateStarted: (state) => {
      state.deactivate = { status: 'pending', error: null };
    },
    deactivateFailed: (state, action: PayloadAction<string>) => {
      state.deactivate = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled deactivation (page calls after the snackbar). */
    deactivateCleared: (state) => {
      state.deactivate = emptyDeactivate;
    },

    // ── Reactivation lifecycle (#659) ──
    /** POST is in flight. */
    reactivateStarted: (state) => {
      state.reactivate = { status: 'pending', error: null, immunised: false };
    },
    /** `immunised`: the profile won't be re-deactivated by future scans. */
    reactivateSucceeded: (state, action: PayloadAction<{ immunised: boolean }>) => {
      state.reactivate = { status: 'succeeded', error: null, immunised: action.payload.immunised };
    },
    reactivateFailed: (state, action: PayloadAction<string>) => {
      state.reactivate = { status: 'failed', error: action.payload, immunised: false };
    },
    /** Acknowledge a settled reactivation (page calls after the snackbar). */
    reactivateCleared: (state) => {
      state.reactivate = emptyReactivate;
    },

    // ── Admin contact verification (#556) ──
    /** PATCH is in flight for one contact; the other toggle stays usable. */
    contactStatusStarted: (state, action: PayloadAction<'email' | 'phone'>) => {
      state.contactStatus = { pending: action.payload, error: null };
    },
    contactStatusFailed: (state, action: PayloadAction<string>) => {
      state.contactStatus = { pending: null, error: action.payload };
    },
    /** Settled; the reload carries the new status, so there is nothing to hold. */
    contactStatusCleared: (state) => {
      state.contactStatus = emptyContactStatus;
    },

    // ── Network tab ──
    commonNetworkLoadStarted: (state) => {
      state.commonNetwork.loading = true;
    },
    /** Also the failure transition; a failed load lands an empty, settled tab. */
    commonNetworkLoaded: (state, action: PayloadAction<CommonNetworkProfile[]>) => {
      state.commonNetwork = { rows: action.payload, loading: false, loaded: true };
    },

    // ── Add to list (admin, #614) ──
    assignableListsLoadStarted: (state) => {
      state.addToList.listsStatus = 'loading';
    },
    assignableListsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.addToList.lists = action.payload;
      state.addToList.listsStatus = 'loaded';
    },
    // ── Type chip (admin, #629) ──
    typeCategoriesLoaded: (state, action: PayloadAction<TypeCategories>) => {
      state.typeCategories = action.payload;
    },

    // ── The admin's "Types" dialog (#652) ──
    typesEditLoadStarted: (state) => {
      state.typesEdit = { ...emptyTypesEdit, status: 'loading' };
    },
    typesEditLoadFailed: (state) => {
      state.typesEdit = { ...emptyTypesEdit, status: 'failed' };
    },
    typesEditStarted: (state, action: PayloadAction<string>) => {
      state.typesEdit.pendingType = action.payload;
      state.typesEdit.error = null;
    },
    /** Every answer carries the profile's types afterwards: loaded, removed or restored. */
    typesEditLoaded: (state, action: PayloadAction<ProfileTypesEdit>) => {
      state.typesEdit = { status: 'loaded', data: action.payload, pendingType: null, error: null };
    },
    typesEditFailed: (state, action: PayloadAction<string>) => {
      state.typesEdit.pendingType = null;
      state.typesEdit.error = action.payload;
    },
    typesEditClosed: (state) => {
      state.typesEdit = emptyTypesEdit;
    },

    // ── A client's "Types" dialog, for their licence (#723) ──
    licenceTypesEditLoadStarted: (state) => {
      state.licenceTypesEdit = { ...emptyLicenceTypesEdit, status: 'loading' };
    },
    licenceTypesEditLoadFailed: (state) => {
      state.licenceTypesEdit = { ...emptyLicenceTypesEdit, status: 'failed' };
    },
    licenceTypesEditStarted: (state, action: PayloadAction<string>) => {
      state.licenceTypesEdit.pendingType = action.payload;
      state.licenceTypesEdit.error = null;
    },
    /** Every answer carries the profile's types afterwards: loaded, added or removed. */
    licenceTypesEditLoaded: (state, action: PayloadAction<LicenceProfileTypesEdit>) => {
      state.licenceTypesEdit = { status: 'loaded', data: action.payload, pendingType: null, error: null };
    },
    licenceTypesEditFailed: (state, action: PayloadAction<string>) => {
      state.licenceTypesEdit.pendingType = null;
      state.licenceTypesEdit.error = action.payload;
    },
    licenceTypesEditClosed: (state) => {
      state.licenceTypesEdit = emptyLicenceTypesEdit;
    },
    assignableListsFailed: (state) => {
      state.addToList.listsStatus = 'failed';
    },
    addToListStarted: (state) => {
      state.addToList = { ...state.addToList, pending: true, error: null, added: null };
    },
    addToListSucceeded: (state, action: PayloadAction<string>) => {
      state.addToList = { ...state.addToList, pending: false, added: action.payload };
    },
    addToListFailed: (state, action: PayloadAction<string>) => {
      state.addToList = { ...state.addToList, pending: false, error: action.payload };
    },
    /** Acknowledge a settled POST (page calls after the snackbar); keeps the loaded lists. */
    addToListCleared: (state) => {
      state.addToList = { ...emptyAddToList, lists: state.addToList.lists, listsStatus: state.addToList.listsStatus };
    },

    // ── Notes tab (#676) ──
    // A new profile starts from nothing; the same profile keeps what it shows, so
    // re-opening the tab refreshes the notes without flashing the spinner.
    notesLoadStarted: (state, action: PayloadAction<string>) => {
      if (state.notes.profileId !== action.payload) {
        state.notes = { ...emptyNotes, profileId: action.payload };
      }
      state.notes.status = 'loading';
      state.notes.error = null;
    },
    notesLoadSucceeded: (state, action: PayloadAction<NotesLoaded>) => {
      if (state.notes.profileId !== action.payload.profileId) return;
      state.notes.notes = action.payload.notes;
      state.notes.status = 'loaded';
    },
    notesLoadFailed: (state, action: PayloadAction<NotesFailed>) => {
      if (state.notes.profileId !== action.payload.profileId) return;
      state.notes.status = 'failed';
      state.notes.error = action.payload.message;
    },
    notesSaveStarted: (state) => {
      state.notes.save = { status: 'pending', error: null };
    },
    // The notes as the server saved them (normalised, author and date) replace the
    // shown ones; only on the profile they belong to.
    notesSaveSucceeded: (state, action: PayloadAction<NotesLoaded>) => {
      state.notes.save = { status: 'succeeded', error: null };
      if (state.notes.profileId !== action.payload.profileId) return;
      state.notes.notes = action.payload.notes;
      state.notes.status = 'loaded';
    },
    notesSaveFailed: (state, action: PayloadAction<string>) => {
      state.notes.save = { status: 'failed', error: action.payload };
    },
    notesSaveCleared: (state) => {
      state.notes.save = emptyNotes.save;
    },

    /** Patch the favourite flag in place (shared FavoriteButton onChange). */
    favoriteChanged: (state, action: PayloadAction<boolean>) => {
      if (state.profile) state.profile.is_favorite = action.payload;
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const profileDetailActions = slice.actions;
export const profileDetailReducer = slice.reducer;
